import React from "react";
import axios from "axios";
import { Image as ImageIcon, Upload, Trash2, X, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const API_BASE = "/api";

export function ImageManager({ onSelect, trigger }) {
  const [open, setOpen] = React.useState(false);
  const [images, setImages] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const { toast } = useToast();

  const fetchImages = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/images`, { withCredentials: true });
      setImages(data.images || []);
    } catch (e) {
      console.error("Failed to load images", e);
    }
  };

  React.useEffect(() => {
    if (open) fetchImages();
  }, [open]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await axios.post(`${API_BASE}/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({ title: "Загружено", description: "Изображение успешно добавлено." });
      fetchImages();
    } catch (e) {
      toast({ variant: "destructive", title: "Ошибка", description: "Не удалось загрузить файл." });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename, e) => {
    e.stopPropagation();
    if (!confirm("Удалить изображение?")) return;
    try {
      await axios.delete(`${API_BASE}/images/${filename}`, { withCredentials: true });
      setImages((prev) => prev.filter((img) => img.filename !== filename));
      toast({ title: "Удалено", description: "Изображение удалено." });
    } catch (e) {
      toast({ variant: "destructive", title: "Ошибка", description: "Не удалось удалить файл." });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <ImageIcon className="mr-2 h-4 w-4" />
            Выбрать из галереи
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex h-[86vh] w-[calc(100vw-1rem)] flex-col p-4 sm:h-[80vh] sm:max-w-4xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Галерея изображений</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-wrap items-center gap-3 border-b py-3 sm:gap-4 sm:py-4">
          <Button disabled={uploading} className="relative">
            {uploading ? "Загрузка..." : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Загрузить файл
              </>
            )}
            <input
              type="file"
              accept="image/*" // Restrict to images
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleUpload}
              disabled={uploading}
            />
          </Button>
          <div className="text-sm text-zinc-500">
            Поддерживаются JPG, PNG, WEBP.
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          {images.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-zinc-500">
              <ImageIcon className="h-12 w-12 mb-2 opacity-20" />
              <div>Нет загруженных изображений</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map((img) => (
                <div
                  key={img.filename}
                  className="group relative aspect-square border rounded-md overflow-hidden bg-zinc-50 cursor-pointer hover:ring-2 hover:ring-zinc-900 transition-all"
                  onClick={() => {
                   if(onSelect) {
                       onSelect(img.url);
                       setOpen(false);
                   }
                  }}
                >
                  <img
                    src={img.url}
                    alt={img.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDelete(img.filename, e)}
                    className="absolute top-1 right-1 p-1 bg-white/90 rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600 shadow-sm"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>

                  <div className="absolute bottom-0 inset-x-0 bg-white/90 p-1 text-[10px] truncate text-center opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.filename}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
